// apps/web/src/features/russia-requests/queries.ts
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import type {
  AddRussiaRequestBidInputWire,
  AddRussiaRequestCarrierInput,
  CreateRussiaRequestFromParentInput,
  CreateRussiaRequestManualInput,
  ListRussiaCarriersResponse,
  PatchRussiaRequestInput,
  RussiaRequest,
  RussiaRequestAuditEntriesQuery,
  RussiaRequestAuditEntriesResponse,
  RussiaRequestCarrier,
  RussiaRequestCarrierBid,
  RussiaRequestCarriersResponse,
  RussiaRequestChatMessage,
  RussiaRequestChatReadBody,
  RussiaRequestChatUnreadSummaryResponse,
  RussiaRequestFile,
  RussiaRequestListQuery,
  RussiaRequestListResponse,
  RussiaRequestManagerOptionsResponse,
  RussiaRequestTransitionKey,
  StorageDownloadUrlResponse,
  UpdateRussiaRequestBidInput,
  UpdateRussiaRequestCarrierInput,
} from '@magimp/shared';

import { listRussiaCarriers } from '../../api/russia-carriers.js';
import { russiaRequestsKeys } from './queryKeys.js';
import {
  addRussiaRequestBid,
  addRussiaRequestCarrier,
  createRussiaRequestFromParent,
  createRussiaRequestManual,
  deleteRussiaRequest,
  deleteRussiaRequestBid,
  deleteRussiaRequestCarrier,
  duplicateRussiaRequest,
  fetchRussiaRequest,
  fetchRussiaRequestChatUnreadSummary,
  listRussiaRequestAuditEntries,
  listRussiaRequestCarriers,
  listRussiaRequestChat,
  listRussiaRequestFiles,
  listRussiaRequestManagerOptions,
  listRussiaRequests,
  markRussiaRequestChatRead,
  markRussiaRequestChatUnread,
  patchRussiaRequest,
  pickRussiaRequestWinner,
  postRussiaRequestChat,
  russiaRequestItemPhotoDownloadUrl,
  transitionRussiaRequest,
  unmarkRussiaRequestChatUnread,
  updateRussiaRequestBid,
  updateRussiaRequestCarrier,
  type RussiaRequestChatListResponse,
  type RussiaTransitionBody,
} from '../../api/russia-requests/index.js';

// WHY (#1664 file-size split): the key factory moved to `queryKeys.ts` and
//   the realtime cache-narrowing helpers to `cacheNarrowing.ts` as cohesive
//   responsibilities; this module keeps the CRUD/read hooks. Re-export both
//   so existing `./queries.js` import paths stay stable.
export { russiaRequestsKeys } from './queryKeys.js';
export {
  narrowInvalidateForRussiaRequest,
  invalidateRussiaUnreadSummariesContaining,
} from './cacheNarrowing.js';

// -- queries ----------------------------------------------------------

export function useRussiaRequestsList(
  filters: Partial<RussiaRequestListQuery> = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery<RussiaRequestListResponse>({
    queryKey: russiaRequestsKeys.list(filters),
    queryFn: ({ signal }) => listRussiaRequests(filters, { signal }),
    enabled: options.enabled ?? true,
  });
}

/**
 * @aiPurpose cursor-paginated russia-requests list — wraps
 *   `GET /api/russia-requests` for the `Load more` interaction on
 *   `RussiaRequestsListPage`. Filters minus `cursor` are part of the
 *   queryKey; changing them starts a fresh walk so a stale cursor never
 *   re-attaches to a different filter set.
 * @aiInvariant `getNextPageParam` returns `undefined` when the server
 *   yields `nextCursor: null`; TanStack interprets that as "no more
 *   pages" so `hasNextPage` is false and the `Load more` button hides.
 */
export function useRussiaRequestsInfinite(
  filters: Partial<RussiaRequestListQuery> = {},
  options: { enabled?: boolean } = {},
) {
  return useInfiniteQuery<
    RussiaRequestListResponse,
    Error,
    InfiniteData<RussiaRequestListResponse>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: russiaRequestsKeys.listInfinite(filters),
    initialPageParam: undefined,
    queryFn: ({ pageParam, signal }) =>
      listRussiaRequests({ ...filters, cursor: pageParam }, { signal }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: options.enabled ?? true,
  });
}

export function useRussiaRequest(id: string | null, options: { enabled?: boolean } = {}) {
  return useQuery<RussiaRequest>({
    queryKey: id ? russiaRequestsKeys.detail(id) : ['russia-request', '__none__'],
    queryFn: ({ signal }) => {
      if (!id) throw new Error('useRussiaRequest called without id');
      return fetchRussiaRequest(id, { signal });
    },
    enabled: options.enabled !== false && id !== null,
  });
}

export function useRussiaRequestAuditEntries(
  id: string,
  filters: Partial<RussiaRequestAuditEntriesQuery> = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery<RussiaRequestAuditEntriesResponse>({
    queryKey: russiaRequestsKeys.auditEntries(id, filters),
    queryFn: ({ signal }) => listRussiaRequestAuditEntries(id, filters, { signal }),
    enabled: options.enabled ?? true,
  });
}

/**
 * @aiPurpose carriers tab — paginated read of the Russia request's
 *   carriers + inlined bid set. The query key sits under the detail
 *   prefix so the realtime detail-invalidator refreshes it on
 *   `russia-requests:updated` alongside the parent row.
 * @aiSecurity backend gates on `russiaRequests.read` plus the parent
 *   visibility predicate (`findVisibleOrThrow`); the hook does not
 *   carry its own client-side gate beyond the drawer's existing
 *   `russiaRequests.read` route guard.
 */
export function useRussiaRequestCarriers(id: string, options: { enabled?: boolean } = {}) {
  return useQuery<RussiaRequestCarriersResponse>({
    queryKey: russiaRequestsKeys.carriers(id),
    queryFn: ({ signal }) => listRussiaRequestCarriers(id, { signal }),
    enabled: options.enabled ?? true,
  });
}

/**
 * @aiPurpose read-only file list for a COMPLEX request's cargo section
 *   (#1548). Backend perm `russiaRequests.read`; the lean drawer only
 *   reads — upload stays in the editor.
 */
export function useRussiaRequestFiles(id: string, options: { enabled?: boolean } = {}) {
  return useQuery<RussiaRequestFile[]>({
    queryKey: russiaRequestsKeys.files(id),
    queryFn: () => listRussiaRequestFiles(id),
    enabled: options.enabled ?? true,
  });
}

/**
 * @aiPurpose resolve one SIMPLE position photo's signed download URL for
 *   the cargo read view (#1548). Fetches on demand and caches the result
 *   in the in-memory query cache for the URL's short TTL.
 * @aiSecurity the returned `downloadUrl` is a bearer-like temporary access
 *   URL; it lives only in the in-memory query cache here. Never log it,
 *   never persist it, never put it in telemetry or other URLs.
 */
export function useRussiaRequestItemPhotoDownloadUrl(
  requestId: string,
  itemId: string,
  objectKey: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery<StorageDownloadUrlResponse>({
    queryKey: russiaRequestsKeys.itemPhotoDownloadUrl(requestId, itemId, objectKey),
    queryFn: () => russiaRequestItemPhotoDownloadUrl(requestId, itemId, objectKey),
    enabled: options.enabled ?? true,
    // The signed URL itself is short-lived; keep the cache window under it
    // so a re-render reuses the URL but a stale link is never served.
    staleTime: 60_000,
  });
}

/**
 * @aiPurpose RU-internal carriers catalog source (#1166) for the
 *   /russia-rates drawer add-carrier picker (#1167). Active rows only —
 *   the picker never shows archived carriers. Caller gates the picker
 *   via the `russiaCarriers.read` permission.
 */
export function useRussiaCarriersCatalog(options: { enabled?: boolean } = {}) {
  return useQuery<ListRussiaCarriersResponse>({
    queryKey: ['russia-carriers-catalog'],
    queryFn: () => listRussiaCarriers({ active: true }),
    enabled: options.enabled !== false,
    staleTime: 5 * 60_000,
  });
}

// WHY (#706): manager picker source. The endpoint is server-scoped to
//   managers visible to the caller; `enabled` is gated client-side on
//   `russiaRequests.scope.all` so a role without that permission does
//   not paint the picker and never issues the request.
export function useRussiaRequestManagerOptions(options: { enabled?: boolean } = {}) {
  return useQuery<RussiaRequestManagerOptionsResponse>({
    queryKey: ['russia-requests', 'manager-options'],
    queryFn: ({ signal }) => listRussiaRequestManagerOptions({ signal }),
    enabled: options.enabled ?? true,
  });
}

// -- mutations --------------------------------------------------------

function useInvalidate() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: russiaRequestsKeys.all });
    if (id) void qc.invalidateQueries({ queryKey: russiaRequestsKeys.detail(id) });
  };
}

export function useCreateRussiaRequestManual() {
  const invalidate = useInvalidate();
  return useMutation<RussiaRequest, Error, CreateRussiaRequestManualInput>({
    mutationFn: (input) => createRussiaRequestManual(input),
    onSuccess: (row) => invalidate(row.id),
  });
}

/**
 * @aiPurpose mutation wrapper around POST /russia-requests/from-parent/:id.
 *   Idempotent on `parentRequestId`: a repeat call with the same parent
 *   returns the existing RussiaRequest unchanged (server-side P2002
 *   race handling). Used by the China-side Russia-rate button (#707).
 */
export function useCreateRussiaRequestFromParent() {
  const invalidate = useInvalidate();
  return useMutation<
    RussiaRequest,
    Error,
    { parentRequestId: string; input?: CreateRussiaRequestFromParentInput }
  >({
    mutationFn: ({ parentRequestId, input = {} }) =>
      createRussiaRequestFromParent(parentRequestId, input),
    onSuccess: (row) => invalidate(row.id),
  });
}

// WHY (#2120): duplicate a Russia request into a new DRAFT. v1 has no
//   server-side idempotency (D-C11) — single-flight is enforced by the caller
//   disabling the action while this mutation is pending. Like create, the new
//   row is absent from every list cache, so invalidate the whole prefix +
//   the new detail.
export function useDuplicateRussiaRequest() {
  const invalidate = useInvalidate();
  return useMutation<RussiaRequest, Error, { id: string }>({
    mutationFn: ({ id }) => duplicateRussiaRequest(id),
    onSuccess: (row) => invalidate(row.id),
  });
}

export function usePatchRussiaRequest(id: string) {
  const invalidate = useInvalidate();
  return useMutation<RussiaRequest, Error, PatchRussiaRequestInput>({
    mutationFn: (body) => patchRussiaRequest(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function useDeleteRussiaRequest(id: string) {
  const invalidate = useInvalidate();
  return useMutation<void, Error, void>({
    mutationFn: () => deleteRussiaRequest(id),
    onSuccess: () => invalidate(id),
  });
}

/**
 * @aiPurpose mutation around POST /russia-requests/:id/carriers. Adds
 *   the catalog carrier to the request's carrier set. On success
 *   invalidates both the carriers sub-query (so the new row paints
 *   immediately) and the request detail (the audit ledger entry tied
 *   to `carrier.add` will surface on the audit tab's next fetch).
 *   Server-side errors are surfaced as `ApiClientError` instances and
 *   should be matched on `code` (404 / 409 / 400), not message text.
 */
export function useAddRussiaRequestCarrier(id: string) {
  const qc = useQueryClient();
  return useMutation<RussiaRequestCarrier, Error, AddRussiaRequestCarrierInput>({
    mutationFn: (input) => addRussiaRequestCarrier(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.carriers(id) });
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.detail(id) });
    },
  });
}

/**
 * @aiPurpose mutation around POST /russia-requests/:id/carriers/:carrierId/bids
 *   for a whole-request (`REQUEST`) bid OR a per-departure-city
 *   (`START_CITY`) rate (#1580). A START_CITY submit carries
 *   `scope: 'START_CITY'` + the start-city `scopeRefId`; the server
 *   UPSERTs on the `(carrier, scopeRefId)` tuple, so re-submitting the
 *   same city updates the rate in place (never a 2nd row). Invalidates the
 *   carriers sub-query so the new/updated bid + flipped `state`
 *   (PENDING → ANSWERED) paint immediately, and the request detail so the
 *   audit tab's next fetch picks up the `bid.add` ledger row. Server-side
 *   errors are `ApiClientError` instances and should be matched on `code`
 *   (404 for an unknown city ref / 409 / 400), not message text.
 */
export function useAddRussiaRequestBid(id: string) {
  const qc = useQueryClient();
  return useMutation<
    RussiaRequestCarrierBid,
    Error,
    { russiaRequestCarrierId: string; input: AddRussiaRequestBidInputWire }
  >({
    mutationFn: ({ russiaRequestCarrierId, input }) =>
      addRussiaRequestBid(id, russiaRequestCarrierId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.carriers(id) });
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.detail(id) });
    },
  });
}

// WHY (#1168): all four carrier/bid mutation hooks share the same
//   invalidation surface (carriers sub-query + request detail audit).
//   The server is the source of truth for `displayCode` /
//   `expiresAt` / `isExpired`, so a refetch is cheaper than an
//   optimistic patch.
export function useUpdateRussiaRequestCarrier(id: string) {
  const qc = useQueryClient();
  return useMutation<
    RussiaRequestCarrier,
    Error,
    { russiaRequestCarrierId: string; input: UpdateRussiaRequestCarrierInput }
  >({
    mutationFn: ({ russiaRequestCarrierId, input }) =>
      updateRussiaRequestCarrier(id, russiaRequestCarrierId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.carriers(id) });
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.detail(id) });
    },
  });
}

export function useDeleteRussiaRequestCarrier(id: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { russiaRequestCarrierId: string }>({
    mutationFn: ({ russiaRequestCarrierId }) =>
      deleteRussiaRequestCarrier(id, russiaRequestCarrierId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.carriers(id) });
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.detail(id) });
    },
  });
}

export function useUpdateRussiaRequestBid(id: string) {
  const qc = useQueryClient();
  return useMutation<
    RussiaRequestCarrierBid,
    Error,
    { russiaRequestCarrierId: string; bidId: string; input: UpdateRussiaRequestBidInput }
  >({
    mutationFn: ({ russiaRequestCarrierId, bidId, input }) =>
      updateRussiaRequestBid(id, russiaRequestCarrierId, bidId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.carriers(id) });
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.detail(id) });
    },
  });
}

export function useDeleteRussiaRequestBid(id: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { russiaRequestCarrierId: string; bidId: string }>({
    mutationFn: ({ russiaRequestCarrierId, bidId }) =>
      deleteRussiaRequestBid(id, russiaRequestCarrierId, bidId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.carriers(id) });
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.detail(id) });
    },
  });
}

/**
 * @aiPurpose mutation around POST /russia-requests/:id/winner (#1544) —
 *   manager-side winner-bid selection that `success` later consumes.
 *   Invalidates both the carriers sub-query (the winner highlight lives
 *   on the carrier list) and the request detail. For REQUEST-scope bids,
 *   the stored `winnerBidId` flips on the request row. For START_CITY-scope
 *   bids (#1580), the `winnerBidId` flips independently per departure city
 *   on the `RussiaRequestStartCity` row. Server-side errors are
 *   `ApiClientError` instances and should be matched on `code` (403 /
 *   404 / 409), not message text.
 * @aiSecurity backend rejects logist winner-pick with 403; callers must
 *   gate the control with `canPickRussiaWinner` (manager/admin only).
 */
export function useRussiaRequestPickWinner(id: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { bidId: string }>({
    mutationFn: ({ bidId }) => pickRussiaRequestWinner(id, bidId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.carriers(id) });
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.detail(id) });
    },
  });
}

export function useTransitionRussiaRequest(id: string) {
  const invalidate = useInvalidate();
  return useMutation<
    RussiaRequest,
    Error,
    { key: RussiaRequestTransitionKey; body?: RussiaTransitionBody }
  >({
    mutationFn: ({ key, body }) => transitionRussiaRequest(id, key, body),
    onSuccess: () => invalidate(id),
  });
}

// -- RF chat (#1067) --------------------------------------------------

/**
 * @aiPurpose paginated RF chat list — wraps `GET /russia-requests/:id/chat`.
 *   Mirrors the China `useChatList` shape so the drawer renderer can stay
 *   identical in behavior.
 * @aiSecurity backend gates on the chat-party predicate
 *   (admin · authoring manager · acting logist); unauthorized callers
 *   receive 404 and the cache stays empty.
 */
export function useRussiaRequestChatList(
  id: string | null,
  query: { cursor?: string; limit?: number } = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery<RussiaRequestChatListResponse>({
    queryKey: [...russiaRequestsKeys.chat(id ?? '__none__'), query],
    queryFn: () => {
      if (!id) throw new Error('useRussiaRequestChatList called without id');
      return listRussiaRequestChat(id, query);
    },
    enabled: options.enabled !== false && id !== null,
  });
}

/**
 * @aiPurpose batch-fetch RF unread-chat summary for the supplied RF ids.
 *   Stays disabled until `russiaRequestIds` is non-empty so an empty list
 *   page does not hit the endpoint. Backend silently omits
 *   invisible/non-existent ids; consumers must treat a missing id as
 *   `{ unreadCount: 0, isMarkedUnread: false }`.
 * @aiInvariant the queryKey is identity-stable for the same id-set (the
 *   key factory stable-sorts), so realtime fan-out must invalidate by
 *   the `chatUnreadSummaryPrefix()` to refresh every cached chunk.
 */
export function useRussiaRequestChatUnreadSummary(
  russiaRequestIds: readonly string[],
  options: { enabled?: boolean } = {},
) {
  return useQuery<RussiaRequestChatUnreadSummaryResponse>({
    queryKey: russiaRequestsKeys.chatUnreadSummary(russiaRequestIds),
    queryFn: ({ signal }) => fetchRussiaRequestChatUnreadSummary(russiaRequestIds, { signal }),
    enabled: (options.enabled ?? true) && russiaRequestIds.length > 0,
  });
}

export function usePostRussiaRequestChat(id: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation<RussiaRequestChatMessage, Error, string>({
    mutationFn: (text) => postRussiaRequestChat(id, text),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: russiaRequestsKeys.chat(id) });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.chat(id) });
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.chatUnreadSummaryPrefix() });
      invalidate(id);
    },
  });
}

/**
 * @aiPurpose advance the actor's RF read pointer to the chronologically
 *   newest visible message. Server runs a DB-side
 *   `GREATEST(stored, messageCreatedAt)` upsert, so the boolean
 *   check-mark surface re-renders without the client guessing.
 * @aiInvariant invalidates BOTH the chat slice (so own-message
 *   `readByCounterparty` flips for the counterparty on the realtime
 *   fan-out) and the unread-summary prefix (so the row's badge updates).
 */
export function useMarkRussiaRequestChatRead(id: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, RussiaRequestChatReadBody>({
    mutationFn: (body) => markRussiaRequestChatRead(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.chat(id) });
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.chatUnreadSummaryPrefix() });
    },
  });
}

export function useMarkRussiaRequestChatUnread(id: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => markRussiaRequestChatUnread(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.chatUnreadSummaryPrefix() });
    },
  });
}

export function useUnmarkRussiaRequestChatUnread(id: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => unmarkRussiaRequestChatUnread(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: russiaRequestsKeys.chatUnreadSummaryPrefix() });
    },
  });
}
